import React from 'react';
import FuzzySearch from 'fuzzy-search';
import { pathOr } from 'ramda';
import styled from 'styled-components';
import { Button, Autocomplete, DropdownSelect } from '@prezi/bento';
import CollaboratorPermission, { PermissionValue } from '../../../utils/permissions';
import Email from '../../../models/Email';
import { createDataLookup, TOOLTIP_ROOT } from '../../const';
import { HighlightOption } from './';
import { localI18n } from '../../../i18n';
const uxCopy = {
  inputPlaceholder: localI18n._("Name or e-mail address"),
  addButton: localI18n._("Add")
};
const FormWrapper = styled.div.withConfig({
  displayName: "AddCollaborator__FormWrapper",
  componentId: "sc-1hz6alu-0"
})(["display:flex;flex-wrap:wrap;& > *:not(:last-child){margin-right:10px;}"]);
const NameEmailWrap = styled.div.withConfig({
  displayName: "AddCollaborator__NameEmailWrap",
  componentId: "sc-1hz6alu-1"
})(["flex:1;"]);
const PermissionsWrap = styled.div.withConfig({
  displayName: "AddCollaborator__PermissionsWrap",
  componentId: "sc-1hz6alu-2"
})(["position:relative;width:160px;"]);
const mapContactsToEmails = contacts => contacts.map(pathOr('', ['email']));
export default (({
  individualAddCollaboratorsPermissions,
  contacts,
  handleAdd,
  collaboratorCount,
  initialEmail
}) => {
  const autoCompleteHandle = React.useRef(null);
  const [email, setEmail] = React.useState(initialEmail);
  const [permission, setPermission] = React.useState(PermissionValue.CAN_VIEW);
  const [fuzzySearch, setFuzzySearch] = React.useState(null);
  const collaboratorPermission = CollaboratorPermission.fromAccessRight(individualAddCollaboratorsPermissions);
  const hasNoAccess = collaboratorPermission.hasNoAccess();
  const contactList = contacts.asList();
  React.useEffect(() => {
    if (contactList.length) {
      setFuzzySearch(new FuzzySearch(contactList, ['name', 'email'], {
        caseSensitive: false,
        sort: true
      }));
    }
  }, [contactList.length]);
  React.useEffect(() => {
    if ((initialEmail === null || initialEmail === void 0 ? void 0 : initialEmail.toString()) !== (email === null || email === void 0 ? void 0 : email.toString())) {
      var _autoCompleteHandle$c;
      (_autoCompleteHandle$c = autoCompleteHandle.current) === null || _autoCompleteHandle$c === void 0 ? void 0 : _autoCompleteHandle$c.clearSelectedItem();
    }
  }, [collaboratorCount]);
  return React.createElement(FormWrapper, null, React.createElement(NameEmailWrap, null, React.createElement(Autocomplete, {
    value: email === null || email === void 0 ? void 0 : email.toString(),
    options: mapContactsToEmails(contactList),
    placeholder: uxCopy.inputPlaceholder,
    portalRoot: TOOLTIP_ROOT,
    onChange: value => setEmail(Email.fromString(value)),
    matchFunction: (inputValue = '') => {
      setEmail(Email.fromString(inputValue));
      return fuzzySearch ? mapContactsToEmails(fuzzySearch.search(inputValue)) : [];
    },
    dummyDropdownItemValue: 'prezi@example.com',
    OptionComponent: ({
      value = '',
      currentInput
    }) => React.createElement(HighlightOption, {
      currentInput: currentInput,
      name: pathOr('', ['byId', value, 'name'], contacts),
      email: Email.unsafeFromString(value)
    }),
    ref: autoCompleteHandle,
    dataLookup: createDataLookup('add-collaborator-input-autocomplete'),
    disabled: hasNoAccess
  })), React.createElement(PermissionsWrap, null, React.createElement(DropdownSelect, {
    options: collaboratorPermission.permissionOptions(),
    value: permission,
    portalRoot: TOOLTIP_ROOT,
    onChange: p => setPermission(p),
    dataLookup: createDataLookup('add-collaborator-permission-select'),
    disabled: hasNoAccess
  })), React.createElement("div", null, React.createElement(Button, {
    size: "medium",
    variant: "default",
    disabled: hasNoAccess || !email,
    onClick: () => email && handleAdd(email, permission),
    "data-lookup": createDataLookup('add-collaborator-save-button')
  }, uxCopy.addButton)));
});